/**
 * Maparea unui „login" venit din alt manager de parole pe tipurile Cerbes.
 * Comună importurilor Bitwarden și 1Password.
 *
 *   - cu parolă            → `password` (username + url în meta, restul criptat)
 *   - fără parolă          → `generic`, ca notițele să nu se piardă
 *   - cu seed TOTP         → în plus, un credential `totp_seed` (Authenticator)
 */
import { isValidBase32, parseOtpauthUrl, TOTP_DEFAULTS } from '@/crypto/totp';
import type {
  ParsedImport,
  TransferCredential,
} from '@/features/vault/transfer/types';

export interface LoginEntry {
  title: string;
  url: string;
  username: string;
  password: string;
  notes: string;
  totp: string;
}

/** Seed-ul Base32 dintr-un câmp TOTP (URI `otpauth://` sau seed simplu), ori `null`. */
export function extractTotpSeed(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^otpauth:/i.test(value)) {
    try {
      const parsed = parseOtpauthUrl(value);
      // Authenticator-ul calculează doar cu parametrii impliciți; un seed cu
      // alți parametri ar da coduri greșite fără niciun semn.
      const standard =
        parsed.algorithm === TOTP_DEFAULTS.algorithm &&
        parsed.digits === TOTP_DEFAULTS.digits &&
        parsed.period === TOTP_DEFAULTS.period;
      return standard ? parsed.secret.toUpperCase() : null;
    } catch {
      return null;
    }
  }
  const seed = value.replace(/[\s-]/g, '').toUpperCase();
  return seed && isValidBase32(seed) ? seed : null;
}

/** Titlul afișat pentru o intrare fără nume: gazda URL-ului sau username-ul. */
export function fallbackTitle(url: string, username: string): string {
  if (url) {
    try {
      return new URL(url).hostname || url;
    } catch {
      return url;
    }
  }
  return username;
}

export function mapLogin(entry: LoginEntry): ParsedImport {
  const title = entry.title || fallbackTitle(entry.url, entry.username);
  const result: ParsedImport = { items: [], skipped: [] };
  const hasContent = entry.password || entry.notes || entry.url || entry.username;

  if (hasContent) {
    const base: TransferCredential = {
      type: entry.password ? 'password' : 'generic',
      title,
      url: entry.url || null,
      username: entry.username || null,
      expires_at: null,
      meta: {},
      payload: {},
    };
    if (entry.password) {
      if (entry.username) base.meta.username = entry.username;
      if (entry.url) base.meta.url = entry.url;
      base.payload.password = entry.password;
    }
    if (entry.notes) base.payload.notes = entry.notes;
    result.items.push(base);
  }

  if (entry.totp) {
    const seed = extractTotpSeed(entry.totp);
    if (seed) {
      const meta: TransferCredential['meta'] = { issuer: title };
      if (entry.username) meta.account = entry.username;
      result.items.push({
        type: 'totp_seed',
        title,
        url: null,
        username: null,
        expires_at: null,
        meta,
        payload: { secret: seed },
      });
    } else {
      result.skipped.push({ title, code: 'totp_unsupported' });
    }
  }

  if (!hasContent && !entry.totp) result.skipped.push({ title, code: 'empty_entry' });
  return result;
}
